import * as React from "react"
import { Check, ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"

// ---------------------------------------------------------------------------
// Searchable Select — drop-in replacement for the shadcn/Radix Select.
// Same public API (Select / SelectTrigger / SelectValue / SelectContent /
// SelectItem / SelectGroup / SelectLabel / SelectSeparator) but every dropdown
// now supports type-to-search out of the box. When the list has > 5 items a
// search input is rendered automatically; otherwise the item list renders as
// a plain menu (visually identical to the old behaviour).
// ---------------------------------------------------------------------------

const SelectContext = React.createContext(null)

const Select = ({
  value,
  defaultValue,
  onValueChange,
  disabled,
  open: openProp,
  onOpenChange,
  children,
  ...rest
}) => {
  const [openState, setOpenState] = React.useState(false)
  const isOpenControlled = openProp !== undefined
  const open = isOpenControlled ? openProp : openState
  const setOpen = React.useCallback(
    (next) => {
      if (!isOpenControlled) setOpenState(next)
      onOpenChange?.(next)
    },
    [isOpenControlled, onOpenChange]
  )

  const [internal, setInternal] = React.useState(defaultValue ?? "")
  const isControlled = value !== undefined
  const currentValue = isControlled ? value : internal

  const [labels, setLabels] = React.useState({})
  const registerLabel = React.useCallback((val, lbl) => {
    if (val == null) return
    const key = String(val)
    setLabels((prev) => (prev[key] === lbl ? prev : { ...prev, [key]: lbl }))
  }, [])

  const handleChange = React.useCallback(
    (newVal) => {
      if (!isControlled) setInternal(newVal)
      onValueChange?.(newVal)
      setOpen(false)
    },
    [isControlled, onValueChange, setOpen]
  )

  const ctx = React.useMemo(
    () => ({
      value: currentValue,
      onValueChange: handleChange,
      open,
      setOpen,
      disabled,
      labels,
      registerLabel,
    }),
    [currentValue, handleChange, open, setOpen, disabled, labels, registerLabel]
  )

  return (
    <SelectContext.Provider value={ctx}>
      <Popover open={open} onOpenChange={setOpen} {...rest}>
        {children}
      </Popover>
    </SelectContext.Provider>
  )
}
Select.displayName = "Select"

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => {
  const ctx = React.useContext(SelectContext) || {}
  return (
    <PopoverTrigger asChild>
      <button
        ref={ref}
        type="button"
        disabled={ctx.disabled}
        data-state={ctx.open ? "open" : "closed"}
        data-placeholder={!ctx.value ? "" : undefined}
        className={cn(
          "flex h-10 w-full items-center justify-between whitespace-nowrap rounded-lg border border-white/40 bg-white px-3 py-2 text-sm shadow-sm ring-offset-white focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 hover:border-white/40 transition-colors",
          className
        )}
        {...props}
      >
        {children}
        <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
      </button>
    </PopoverTrigger>
  )
})
SelectTrigger.displayName = "SelectTrigger"

const SelectValue = ({ placeholder, className, children }) => {
  const ctx = React.useContext(SelectContext) || {}
  const hasValue = ctx.value !== undefined && ctx.value !== null && ctx.value !== ""
  const label = hasValue ? ctx.labels?.[String(ctx.value)] : null
  return (
    <span
      className={cn(
        "truncate text-left flex-1",
        !hasValue && "text-neutral-500",
        className
      )}
    >
      {hasValue ? (label ?? children ?? String(ctx.value)) : (placeholder ?? children ?? "")}
    </span>
  )
}
SelectValue.displayName = "SelectValue"

// Walk children to count how many <SelectItem> exist (recursively through
// SelectGroup / fragments). Threshold controls when we show the search input.
function countSelectItems(children) {
  let n = 0
  const walk = (nodes) => {
    React.Children.forEach(nodes, (child) => {
      if (!React.isValidElement(child)) return
      const displayName = child.type?.displayName
      if (displayName === "SelectItem") {
        n += 1
      } else if (child.props?.children) {
        walk(child.props.children)
      }
    })
  }
  walk(children)
  return n
}

const SelectContent = React.forwardRef(
  (
    {
      className,
      children,
      align = "start",
      side,
      sideOffset = 4,
      searchable,
      searchPlaceholder = "Search...",
      emptyMessage = "No results found.",
      position, // legacy shadcn prop – ignored
      ...props
    },
    ref
  ) => {
    const itemCount = React.useMemo(() => countSelectItems(children), [children])
    const showSearch = searchable ?? itemCount > 5

    return (
      <PopoverContent
        ref={ref}
        align={align}
        side={side}
        sideOffset={sideOffset}
        className={cn(
          "z-50 w-[var(--radix-popover-trigger-width)] min-w-[8rem] p-0 rounded-lg border border-white/40 bg-white text-[#1a1a2e] shadow-lg overflow-hidden",
          className
        )}
        {...props}
      >
        <Command shouldFilter={showSearch}>
          {showSearch && (
            <CommandInput
              placeholder={searchPlaceholder}
              className="h-9 border-0 focus:ring-0"
              data-testid="select-search-input"
            />
          )}
          <CommandList className="max-h-[300px]">
            {showSearch && <CommandEmpty>{emptyMessage}</CommandEmpty>}
            {children}
          </CommandList>
        </Command>
      </PopoverContent>
    )
  }
)
SelectContent.displayName = "SelectContent"

const SelectGroup = React.forwardRef(({ className, ...props }, ref) => (
  <CommandGroup ref={ref} className={cn("p-1", className)} {...props} />
))
SelectGroup.displayName = "SelectGroup"

const SelectLabel = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "px-2 py-1.5 text-xs font-semibold text-neutral-500 uppercase tracking-wider",
      className
    )}
    {...props}
  />
))
SelectLabel.displayName = "SelectLabel"

function extractLabel(children) {
  const parts = []
  const collect = (node) => {
    if (node == null || typeof node === "boolean") return
    if (typeof node === "string" || typeof node === "number") {
      parts.push(String(node))
      return
    }
    if (Array.isArray(node)) {
      node.forEach(collect)
      return
    }
    if (React.isValidElement(node)) collect(node.props?.children)
  }
  collect(children)
  return parts.join(" ").replace(/\s+/g, " ").trim()
}

const SelectItem = React.forwardRef(
  ({ className, children, value, disabled, ...props }, ref) => {
    const ctx = React.useContext(SelectContext) || {}
    const isSelected = ctx.value != null && String(ctx.value) === String(value)

    const label = React.useMemo(() => extractLabel(children), [children])

    React.useEffect(() => {
      if (value != null && ctx.registerLabel) {
        ctx.registerLabel(value, label || String(value))
      }
    }, [value, label, ctx.registerLabel])

    // cmdk requires unique item values. Prefix with the app value plus label so
    // typing either the value key or the human-readable label filters correctly.
    const cmdkValue = React.useMemo(() => {
      const v = value == null ? "" : String(value)
      const l = label || ""
      return `${v}::${l}` || "__empty__"
    }, [value, label])

    return (
      <CommandItem
        ref={ref}
        value={cmdkValue}
        keywords={label ? [label] : []}
        disabled={disabled}
        onSelect={() => {
          if (!disabled) ctx.onValueChange?.(value)
        }}
        className={cn(
          "relative flex w-full cursor-pointer select-none items-center rounded-xl py-2 pl-3 pr-8 text-sm outline-none data-[selected=true]:bg-pink-50 data-[selected=true]:text-pink-900 aria-selected:bg-pink-50 aria-selected:text-pink-900 hover:bg-white/60 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 transition-colors",
          className
        )}
        {...props}
      >
        <span className="flex-1 truncate">{children}</span>
        {isSelected && (
          <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
            <Check className="h-4 w-4" />
          </span>
        )}
      </CommandItem>
    )
  }
)
SelectItem.displayName = "SelectItem"

const SelectSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <CommandSeparator ref={ref} className={cn("-mx-1 my-1 h-px bg-neutral-200", className)} {...props} />
))
SelectSeparator.displayName = "SelectSeparator"

// Legacy no-ops kept so any code still importing them doesn't blow up.
const SelectScrollUpButton = () => null
const SelectScrollDownButton = () => null

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
